import { all, call, delay, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { windowResizeEvent } from '@elab/core/utils/events';
import analytics from '@elab/core/utils/analytics';
import { selectors as configSelectors } from '@elab/core/store/config';
import isEmpty from '@elab/core/utils/isEmpty';
import { actions as registrationActions } from '@elab/components/connected/forms/RegistrationForm/redux';

import Breakpoints from '../../utils/Breakpoints';
import {
    colorVariantSelector,
    constants,
    dialogConstants,
    dialogWithProductDetailsName,
    sizeVariantSelector,
} from '../../utils/constants';
import { getCustomHeaderScrollHeight } from '../config/selectors';
import * as storage from '../../utils/storage';
import { globalTypes } from '../types';
import { types as globalSagaTypes } from '../global';
import { actions as productActions } from '../product';
import { getUrlParams } from '../../utils/url';
import { actions as socialActions } from '../social';
import { selectors as authSelectors } from '../auth';

import { actions, selectors, types, constants as uiConstants } from '.';

const POP_UP_LIST = ['.step--mask'];

export function* onWindowResize({ payload = {} }) {
    const { width, height } = payload;
    yield delay(300);
    yield put(actions.setWindowSize({ width, height }));
    window.document.documentElement.style.setProperty('--vh', `${height}px`);
}

function* watchScreenZoom() {
    function getRatios() {
        return {
            baselineRatio: window.screen.width / window.innerWidth,
            devicePixelRatio: window.devicePixelRatio || 1,
        };
    }

    function checkZoomRatio() {
        const { baselineRatio, devicePixelRatio } = getRatios();
        if (baselineRatio >= 3 && devicePixelRatio >= 3) {
            window.document.body.classList.add('zoom-ratio-3plus');
        }
    }

    yield all([
        call(checkZoomRatio),
        takeEvery(globalTypes.PAGE_LOADED, checkZoomRatio),
        takeLatest(types.WINDOW_RESIZE, function* () {
            yield delay(300);
            const { baselineRatio, devicePixelRatio } = getRatios();
            if (baselineRatio >= 3 && devicePixelRatio >= 3) {
                window.document.body.classList.add('zoom-ratio-3plus');
            } else {
                window.document.body.classList.remove('zoom-ratio-3plus');
            }
        }),
    ]);
}

export function* onOpenDialogByName({ payload: { openPopupViaUrl, name } }) {
    const currentOpenedDialog = yield select(selectors.getDialogName);

    if (currentOpenedDialog === 'product-cart-popup') {
        yield call(analytics.handleTileAddToCart);
    }

    if (currentOpenedDialog === uiConstants.imagePreviewPopupName) {
        // Is needed to refresh the images swiper slider
        yield delay(30);
        yield call(windowResizeEvent);
    }

    if (currentOpenedDialog === dialogWithProductDetailsName) {
        // Is needed to refresh the images swiper slider on opened popup
        yield delay(30);
        yield put(actions.addElementsToRefresh([sizeVariantSelector, colorVariantSelector]));
    }

    if (openPopupViaUrl && currentOpenedDialog === name) {
        yield call(togglePopupParams, name, true);
    }
}

export function* togglePopupParams(name, method) {
    const urlParams = new URLSearchParams(window.location.search);

    if (method) {
        urlParams.set(dialogConstants.getParamName, name);
    } else {
        urlParams.delete(dialogConstants.getParamName);
    }

    const addHistoryPushState = yield select(configSelectors.addHistoryPushState);
    if (addHistoryPushState) {
        const newParams = urlParams.toString().length ? `?${urlParams.toString()}` : '';
        const newUrl = `${window.location.origin}${window.location.pathname}${newParams}`;

        window.history.pushState({ path: newUrl }, '', newUrl);
    }
}

export function* onCloseDialogByName({ payload }) {
    const isEqual = yield select(selectors.getDialogState, payload.name || payload);
    if (isEqual) {
        yield put(actions.closeDialog());
        yield call(togglePopupParams, payload.name);
    }
    if (payload === 'product-video-popup') {
        yield put(productActions.clearProductVideo());
    }
    if (payload === 'facebook-dialog' || payload === 'google-dialog') {
        const fbAuthId = yield select(authSelectors.getFbAuthId);
        const facebookButtonSelector = '.fb-lgn-btn';
        const facebookLoginDisableSelector = 'social-login--disable';
        const googleButtonSelector = '.social-login--google';
        const googleLoginDisableSelector = 'google-login--disable';
        sessionStorage.removeItem(`fbssls_${fbAuthId}`);
        sessionStorage.removeItem('googleAuthToken');
        yield call(resetSocialData, facebookButtonSelector, facebookLoginDisableSelector);
        yield call(resetSocialData, googleButtonSelector, googleLoginDisableSelector);
    }
}

export function* resetSocialData(socialButtonSelector, socialLoginDisableSelector) {
    yield put(socialActions.setInitialSocialInfo());
    yield put(registrationActions.setDataFieldValue('socialProfileId', null, true));
    yield put(registrationActions.setDataFieldValue('socialProfileAccessToken', null, true));
    yield put(registrationActions.setDataFieldValue('socialProfileProvider', null, true));
    const socialLgnBtnNode = document.querySelectorAll(socialButtonSelector);
    socialLgnBtnNode.forEach(element => {
        element.classList.remove(socialLoginDisableSelector);
    });
}

/**
 * Return crossbrowser scrollTop position
 */
export function getScrollTop() {
    const { documentElement, body } = window.document;
    return documentElement.scrollTop || body.scrollTop || 0;
}

/**
 * @param {HTMLElement} targetElement - the element to which scrolled to
 * @param {number} [shift] - extra space from the top of the visible area
 */
export function scrollToTop({
    targetElement,
    baseElement = window,
    shift = 0,
    windowSize,
    isHeaderStickyOnScrollDown,
}) {
    const { documentElement, body } = window.document;
    const scrollTop = documentElement.scrollTop || body.scrollTop || baseElement.scrollTop || 0;
    const stickyRowElement = document.querySelector('e2-sticky-row');
    const isMobile = Breakpoints.isMobile(windowSize.width);
    const headerHeight = isMobile
        ? constants.headerScrolledHeightMobile
        : constants.headerScrolledHeightDesktop;

    const distanceFromTop = targetElement.getBoundingClientRect().top;

    const shouldIgnoreHeader =
        distanceFromTop > 0 &&
        !isEmpty(isHeaderStickyOnScrollDown) &&
        ((!isHeaderStickyOnScrollDown.desktop && !isMobile) ||
            (!isHeaderStickyOnScrollDown.mobile && isMobile));
    const gap =
        (shouldIgnoreHeader ? 0 : headerHeight) +
        (stickyRowElement && !isMobile ? constants.stickyRowHeight : 0);

    baseElement.scrollTo({
        top: distanceFromTop + scrollTop - gap - shift,
        behavior: 'smooth',
    });
}

export function* onScrollToTarget({ payload }) {
    const { target, shift = 0 } = payload;
    const targetElement = typeof target === 'string' ? document.querySelector(target) : target;
    const windowSize = yield select(selectors.getWindowSize);
    if (!targetElement) {
        return;
    }
    const customHeader = yield select(getCustomHeaderScrollHeight);
    const { documentElement, body } = window.document;
    const baseElement = window;
    const scrollTop = documentElement.scrollTop || body.scrollTop || baseElement.scrollTop || 0;
    if (customHeader && Object.keys(customHeader).length > 0) {
        let headerHeight = 0;
        let isMobileOnly = false;

        if (customHeader.headerScrolledHeightMobileOnly) {
            isMobileOnly = Breakpoints.isMobileOnly(windowSize.width);
            headerHeight = isMobileOnly
                ? customHeader.headerScrolledHeightMobileOnly
                : headerHeight;
        }

        if (
            !isMobileOnly &&
            customHeader.headerScrolledHeightDesktop &&
            customHeader.headerScrolledHeightMobile
        ) {
            const isMobile = Breakpoints.isMobile(windowSize.width);
            headerHeight = isMobile
                ? customHeader.headerScrolledHeightMobile
                : customHeader.headerScrolledHeightDesktop;
        }

        baseElement.scrollTo({
            top:
                targetElement.getBoundingClientRect().top -
                shift +
                scrollTop -
                (scrollTop > 0 && customHeader.shiftToSubtract
                    ? headerHeight - customHeader.shiftToSubtract
                    : headerHeight),
            behavior: 'smooth',
        });
    }
}

export function smoothScrollIntoView({ element, options = {}, scrollRelativeTo }) {
    if (!element) {
        return;
    }

    const targetElement = typeof element === 'string' ? document.querySelector(element) : element;
    const params = {
        behavior: 'smooth',
        block: 'start',
        ...options,
    };

    const scrollRelativeToElement =
        scrollRelativeTo ||
        (document.body.hasAttribute('prevent-scroll-for-popup') &&
            targetElement.closest('.step--mask')) ||
        window;

    const { top, height } = targetElement.getBoundingClientRect();
    if (params.block === 'start') {
        targetElement.scrollIntoView(params);
    } else {
        let correction = 0;

        if (params.block === 'center') {
            correction = (height - window.innerHeight) / 2;
        } else if (params.block === 'end') {
            correction = height - window.innerHeight;
        }

        scrollRelativeToElement.scrollTo({
            ...params,
            top: getScrollTop() + top + correction,
        });
    }
}

export function* onScrollToCenter({ payload = {} }) {
    const { element, options = {}, scrollRelativeTo } = payload;
    const scrollOptions = {
        block: 'center',
        behavior: 'smooth',
        ...options,
    };
    const ignoreCorrection = yield select(configSelectors.getIgnoreCorrection);

    if (element) {
        smoothScrollIntoView({
            element,
            scrollRelativeTo,
            options: scrollOptions,
            ignoreCorrection,
        });
    } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
}

export function* onScrollToTop({ payload }) {
    const { target, baseElement, shift } = payload;
    const targetElement = typeof target === 'string' ? document.querySelector(target) : target;
    const windowSize = yield select(selectors.getWindowSize);

    if (!targetElement) {
        return;
    }

    const isHeaderStickyOnScrollDown = yield select(configSelectors.isHeaderStickyOnScrollDown);
    const scrollArgs = {
        targetElement,
        baseElement,
        shift,
        windowSize,
        isHeaderStickyOnScrollDown,
    };
    if (document.readyState !== 'complete') {
        window.addEventListener('load', () => scrollToTop(scrollArgs));
    } else {
        yield call(scrollToTop, scrollArgs);
    }
}

export function onScrollIntoView({ payload }) {
    smoothScrollIntoView(payload);
}

export function* onCheckScroll({ payload: { target } }) {
    const options = { target };
    const { top, height } = target.getBoundingClientRect();
    const isDialogOpened = (yield select(selectors.getDialog)).opened;
    const panel = target.querySelector('.datepicker__panel-wrapper');
    const popUpElements = POP_UP_LIST.map(el => document.querySelector(el));

    const panelTransitionDuration = parseFloat(getComputedStyle(panel).transitionDuration) * 1000;
    yield delay(panelTransitionDuration);

    const isDatepickerVisible = window.innerHeight - (top + height + panel.offsetHeight) > 0;

    if (isDialogOpened || isDatepickerVisible) {
        return;
    }

    const targetContainer = popUpElements.find(el => el && el.querySelector('e2-datepicker'));
    if (targetContainer) {
        options.baseElement = targetContainer;
    }

    yield put(actions.scrollToTop(options));
}

export function* scrollToAppliedFilter({ payload: uid }) {
    // because of DOM manipulation need to wait
    yield delay(30);

    const filter = [...document.querySelectorAll('.plp-filters--mobile e2-plp-filter')].find(
        i => i.uid === uid,
    );
    const target = filter && filter.closest('e2-accordion');
    if (target) {
        smoothScrollIntoView({
            element: target,
            options: {
                behavior: 'smooth',
            },
        });
    }
}

export function* onAccordionClick({ payload }) {
    if (['sorting-bottom', 'items-per-page-bottom'].includes(payload.name)) {
        // Wait for the animation end
        yield delay(350);
        const target = document.querySelector('e2-plp-filter-paginator[bottom]');
        smoothScrollIntoView({
            element: target,
            options: { block: 'end' },
        });
    }
}

export function* onCloseModal() {
    yield put(actions.closeModal());
}

export function* addProductReview() {
    const bvInitialized = yield select(selectors.isBVInitialized);
    if (!bvInitialized) {
        yield take(types.BV_INITIALIZE);
    }
    window.$BV.ui('rr', 'submit_review');
}

export function* extractPlpViewType() {
    const plpViewTypes = yield call(storage.get, storage.key.plpViewType, true);
    yield put(actions.setPlpViewTypes(plpViewTypes));
}

export function* savePlpViewType({ payload: { id, type } }) {
    // TODO: sync with storage should be automatic or centralized
    const plpViewTypes = {
        ...(yield select(selectors.getPlpViewTypes)),
        [id || 'default']: type,
    };

    yield call(storage.set, storage.key.plpViewType, plpViewTypes, true);
}

export function* showGreetings({ payload: { name } }) {
    let shouldShowGreetings = yield call(storage.get, storage.key.showGreetings);
    if (shouldShowGreetings) {
        yield put(actions.openDialog({ name }));
        yield call(storage.remove, storage.key.showGreetings);
    }
}

function* openPopupByUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams) {
        const popupName = urlParams.get(dialogConstants.getParamName);
        if (popupName) {
            // needs auth check
            if (popupName === 'review-popup') {
                return;
            }
            yield put(actions.openDialog({ name: popupName }));
        }
    }
}

export function* notifyIframe({ payload }) {
    const iframes = yield select(selectors.getRegisteredIframes);
    iframes.forEach(i => {
        const iframeEl = document.getElementById(i.iframeId);
        if (iframeEl) {
            iframeEl.contentWindow.postMessage(payload, i.iframeDomain);
        }
    });
}

export function* onCloseDialog() {
    const forceClick = yield select(selectors.getForceOpen);
    if (forceClick) {
        yield put(actions.navigateToPDP({ forceClick: false }));
    }
}

export function* onShowAddVoucherForm({ payload: { isVoucher } }) {
    if (isVoucher === 'voucher') {
        yield spawn(analytics.handlePromoAddStartEvent);
    }
}

function* openLightPopup({ payload }) {
    const { urlParamNames, openIf } = payload || {};
    const params = yield call(getUrlParams, urlParamNames);
    if (params && params[openIf]) {
        yield put(actions.openLightPopup({ ...payload, ...params }));
    }
}

export default [
    takeLatest(globalTypes.INITIALIZE, extractPlpViewType),
    takeEvery([types.OK_MODAL, types.CANCEL_MODAL], onCloseModal),
    takeLatest(types.WINDOW_RESIZE, onWindowResize),
    takeEvery(types.SCROLL_TO_TOP, onScrollToTop),
    takeEvery(types.SCROLL_TO_CENTER, onScrollToCenter),
    takeEvery(types.SCROLL_INTO_VIEW, onScrollIntoView),
    takeEvery(types.NORMALIZE_DATEPICKER, onCheckScroll),
    takeLatest(types.ACCORDION_OPEN, onAccordionClick),
    takeEvery(types.OPEN_DIALOG, onOpenDialogByName),
    takeEvery(types.CLOSE_DIALOG, onCloseDialog),
    takeEvery(types.CLOSE_DIALOG_BY_NAME, onCloseDialogByName),
    takeEvery(types.SCROLL_TO_APPLIED_FILTER, scrollToAppliedFilter),
    takeLatest(types.ADD_PRODUCT_REVIEW, addProductReview),
    takeLatest(types.SET_PLP_VIEW_TYPE, savePlpViewType),
    takeLatest(types.SHOW_GREETINGS, showGreetings),
    takeEvery(globalSagaTypes.PAGE_LOAD, openPopupByUrl),
    takeEvery(types.NOTIFY_IFRAME, notifyIframe),
    takeEvery(types.SCROLL_TO_TARGET, onScrollToTarget),
    takeEvery(types.SHOW_ADD_VOUCHER_FORM, onShowAddVoucherForm),
    takeEvery(types.CHECK_OPEN_LIGHT_POPUP, openLightPopup),
    watchScreenZoom(),
];
